import React, { useRef } from 'react';
// Real framer-motion on purpose: this section keeps its full animation on
// phones too — the portrait entrance IS the founder moment. MotionConfig
// below overrides the app-wide phone kill-switch for this subtree only.
import { motion, MotionConfig, useTransform, useSpring, useInView } from 'framer-motion';
import { useViewportProgress } from '../../lib/useViewportProgress';

/**
 * The origin, told like an editorial profile: the founder's portrait anchors
 * the section, the −32 kg chip floats off its corner, and the before/after
 * photos hang underneath as small pinned snapshots — evidence, not the show.
 * The story text scrolls past on the right, revealing line by line.
 */

// True only once the image genuinely decodes — the server answers missing
// files with the SPA's index.html (HTTP 200), so status codes lie.
// The probe downloads the full file, so it waits for `enabled` — otherwise
// every probed image competes with the hero on page load.
const useImageExists = (src, enabled = true) => {
  const [ok, setOk] = React.useState(false);
  React.useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    const im = new Image();
    im.onload = () => alive && setOk(true);
    im.onerror = () => alive && setOk(false);
    im.src = src;
    return () => {
      alive = false;
    };
  }, [src, enabled]);
  return ok;
};

const FounderAvatar = ({ enabled = true }) => {
  const hasPhoto = useImageExists('/founder/portrait.webp', enabled);
  return hasPhoto ? (
    <img
      src="/founder/portrait.webp"
      alt="Prajyot Rote"
      className="w-16 h-16 md:w-[72px] md:h-[72px] rounded-full object-cover border-2 border-ston-purple/50 shadow-lg shadow-ston-purple/30"
    />
  ) : (
    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-ston-purple to-ston-purple-light flex items-center justify-center shadow-lg shadow-ston-purple/30">
      <span className="text-white font-bold text-xl">P</span>
    </div>
  );
};

// Scroll-driven paragraph reveal
const RevealText = ({ children, scrollProgress, startAt, endAt }) => {
  const opacity = useTransform(scrollProgress, [startAt, endAt], [0.14, 1]);
  const y = useTransform(scrollProgress, [startAt, endAt], [10, 0]);
  return <motion.div style={{ opacity, y }}>{children}</motion.div>;
};

/* A small pinned snapshot — the physical-photo look for the evidence pair */
const Snapshot = ({ src, alt, label, rotate, className = '', delay, isInView }) => (
  <motion.figure
    className={`w-[96px] md:w-[116px] rounded-xl border border-white/[0.14] bg-[#16161d] p-1.5 pb-1 shadow-[0_18px_40px_-12px_rgba(0,0,0,0.8)] ${className}`}
    style={{ rotate }}
    initial={{ opacity: 0, y: 22, scale: 0.9 }}
    animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
    transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
  >
    <img
      src={src}
      alt={alt}
      className="w-full aspect-[4/5] rounded-lg object-cover"
      loading="lazy"
    />
    <figcaption className="py-1 text-center font-mono text-[9.5px] uppercase tracking-[2px] text-ston-text-muted">
      {label}
    </figcaption>
  </motion.figure>
);

/* The editorial portrait with the evidence pinned to it */
const PortraitCard = ({ reduce, isInView, hasEvidence }) => (
  <div className="relative w-full max-w-[420px] mb-16 lg:mb-0">
    {/* Ambient glow */}
    <div
      aria-hidden="true"
      className="absolute -inset-8 rounded-[40px] pointer-events-none"
      style={{
        background:
          'radial-gradient(closest-side, rgba(147,51,234,0.24), transparent 72%)',
        }}
    />

    <motion.div
      className="relative rounded-3xl overflow-hidden border border-white/[0.1] aspect-[4/5]"
      style={{ boxShadow: '0 40px 90px -30px rgba(0,0,0,0.85)' }}
      initial={{ opacity: 0, y: 34 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
    >
      <img
        src="/founder/portrait.webp"
        alt="Prajyot Rote, founder of STON.FIT"
        className="absolute inset-0 w-full h-full object-cover object-top"
      />
      {/* Bottom veil + nameplate */}
      <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/80 via-black/25 to-transparent" />
      <div className="absolute bottom-0 inset-x-0 p-5 md:p-6">
        <p className="font-outfit font-extrabold italic text-white text-[20px] leading-tight">
          Prajyot Rote
        </p>
        <p className="mt-0.5 font-mono text-[10.5px] uppercase tracking-[2.5px] text-white/70">
          Founder · STON.FIT
        </p>
      </div>
    </motion.div>

    {/* −32 kg chip, floating off the top-right corner */}
    <motion.div
      className="absolute -top-5 -right-3 md:-right-7 z-10"
      initial={{ opacity: 0, y: 14, scale: 0.9 }}
      animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
      transition={{ duration: 0.7, delay: 0.45, ease: [0.16, 1, 0.3, 1] }}
    >
      <motion.div
        animate={reduce ? {} : { y: [0, -6, 0] }}
        transition={reduce ? {} : { duration: 6, repeat: Infinity, ease: 'easeInOut' }}
        className="rounded-2xl border border-white/[0.12] bg-white/[0.07] backdrop-blur-xl px-4 py-2.5 text-left"
        style={{ rotate: 2, boxShadow: '0 20px 50px -15px rgba(0,0,0,0.7)' }}
      >
        <span className="block font-outfit font-extrabold italic text-ston-text text-[22px] leading-none tabular-nums">
          −32 kg
        </span>
        <span className="mt-1 block text-[10.5px] uppercase tracking-[1.5px] text-ston-text-muted">
          Lost the hard way
        </span>
      </motion.div>
    </motion.div>

    {/* The evidence: before/after snapshots pinned at the bottom edge */}
    {hasEvidence && (
      <div className="absolute -bottom-12 left-2 md:-left-6 flex items-start z-10">
        <Snapshot
          src="/founder/then.webp"
          alt="Prajyot before the journey"
          label="Then"
          rotate={-6}
          delay={0.6}
          isInView={isInView}
        />
        <Snapshot
          src="/founder/after.jpg"
          alt="Prajyot today"
          label="Now"
          rotate={5}
          delay={0.75}
          isInView={isInView}
          className="-ml-3 mt-4"
        />
      </div>
    )}
  </div>
);

/* Fallback when photos aren't on disk: the animated counter card */
const CounterCard = ({ isInView }) => {
  const [displayValue, setDisplayValue] = React.useState(0);
  React.useEffect(() => {
    if (!isInView) return;
    let start;
    const animate = (t) => {
      if (!start) start = t;
      const p = Math.min((t - start) / 2200, 1);
      setDisplayValue(Math.floor((1 - Math.pow(2, -10 * p)) * 32));
      if (p < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [isInView]);
  return (
    <div className="relative bg-ston-card border border-ston-border rounded-3xl p-10 text-center max-w-[380px]">
      <div className="text-7xl font-extrabold gradient-text mb-3" style={{ letterSpacing: '-3px' }}>
        −{displayValue}
        <span className="text-5xl">kg</span>
      </div>
      <div className="text-lg text-ston-text-secondary">Where it all started</div>
    </div>
  );
};

const storyParagraphs = [
  {
    text: (
      <>
        It started with a personal battle —{' '}
        <span className="text-ston-purple-light font-semibold">32 kilograms lost</span>,
        countless early mornings, and the realization that no app out there truly
        understood the journey.
      </>
    ),
    startProgress: 0.18,
  },
  {
    text: 'STON.FIT is built by a trainer who codes and a developer who trains. Every feature exists because it was needed, not because it looked good on a pitch deck.',
    startProgress: 0.32,
  },
  {
    text: (
      <>
        We're not building another calorie counter. We're building the{' '}
        <span className="text-ston-purple-light font-semibold">
          companion that connects everything
        </span>{' '}
        — so you understand your body while you still have choices.
      </>
    ),
    startProgress: 0.46,
  },
];

const FounderStory = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });
  // Honor only a real OS-level reduced-motion setting, not the phone switch
  const reduce =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // Probes download full images — hold them until the section is approaching
  const nearView = useInView(sectionRef, { once: true, margin: '800px' });
  // Hooks must all run every render — no short-circuiting
  const hasBefore = useImageExists('/founder/then.webp', nearView);
  const hasAfter = useImageExists('/founder/after.jpg', nearView);
  const hasPortrait = useImageExists('/founder/portrait.webp', nearView);
  const hasEvidence = hasBefore && hasAfter;

  const scrollYProgress = useViewportProgress(sectionRef);
  const smoothProgress = useSpring(scrollYProgress, { stiffness: 90, damping: 28 });

  // Ghost numeral parallax
  const bgY = useTransform(smoothProgress, [0, 1], [120, -120]);
  const bgOpacity = useTransform(smoothProgress, [0, 0.3, 0.7, 1], [0, 0.14, 0.14, 0]);

  return (
    <MotionConfig reducedMotion="user">
    <section
      id="story"
      ref={sectionRef}
      className="relative py-24 md:py-36 bg-ston-bg-alt overflow-hidden"
      data-testid="founder-story-section"
    >
      {/* Ghost 32 */}
      <motion.div
        aria-hidden="true"
        className="absolute top-1/3 left-[4%] font-outfit font-black italic select-none pointer-events-none leading-none"
        style={{
          fontSize: 'clamp(180px, 24vw, 340px)',
          y: reduce ? 0 : bgY,
          opacity: bgOpacity,
          color: 'transparent',
          WebkitTextStroke: '2px rgba(147, 51, 234, 0.35)',
        }}
      >
        32
      </motion.div>

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-24 items-start">

          {/* Pinned portrait */}
          <div className="flex justify-center lg:justify-start lg:sticky lg:top-28 self-start order-2 lg:order-1">
            {hasPortrait ? (
              <PortraitCard reduce={reduce} isInView={isInView} hasEvidence={hasEvidence} />
            ) : (
              <CounterCard isInView={isInView} />
            )}
          </div>

          {/* Story */}
          <div className="order-1 lg:order-2 lg:py-10">
            <motion.span
              className="section-label inline-block"
              initial={{ opacity: 0, x: -20 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              THE ORIGIN
            </motion.span>

            <motion.h2
              className="mt-4 font-outfit font-extrabold italic uppercase leading-[1.02]"
              style={{ fontSize: 'clamp(28px, 3.6vw, 46px)', letterSpacing: '-0.025em' }}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <span className="block text-ston-text">This app wasn't</span>
              <span className="block gradient-text">born in a boardroom.</span>
            </motion.h2>

            <div className="mt-10 space-y-8">
              {storyParagraphs.map((item, index) => (
                <RevealText
                  key={index}
                  scrollProgress={smoothProgress}
                  startAt={item.startProgress}
                  endAt={item.startProgress + 0.12}
                >
                  <p className="text-[17px] md:text-[18px] text-ston-text-secondary leading-relaxed max-w-[52ch]">
                    {item.text}
                  </p>
                </RevealText>
              ))}
            </div>

            {/* Signature */}
            <motion.div
              className="mt-12 flex items-center gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.7 }}
            >
              <FounderAvatar enabled={nearView} />
              <div>
                <p className="font-outfit text-xl md:text-2xl font-bold text-ston-text">Prajyot Rote</p>
                <p className="mt-0.5 text-[15px] text-ston-text-secondary">Founder, STON.FIT</p>
              </div>
              <motion.div
                className="h-px flex-1 max-w-[100px] ml-2"
                style={{
                  background: 'linear-gradient(to right, var(--ston-purple), transparent)',
                }}
                initial={{ scaleX: 0 }}
                animate={isInView ? { scaleX: 1 } : {}}
                transition={{ duration: 0.8, delay: 0.9 }}
              />
            </motion.div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-ston-bg to-transparent pointer-events-none" />
    </section>
    </MotionConfig>
  );
};

export default FounderStory;
